// Speech-bubble callout anchored to the adventurer illustration.
// Fully controlled: beat + hintTier come from the parent. Hint reveal is
// driven by an explicit 💡 Tipp button in ActionRow, not a timer.

import { useEffect, useMemo, useState } from 'react';
import { resolveCallout, type Beat } from './content';
import { BUBBLE_MAX_CHARS, FADE_SWAP_MS, truncate } from './config';

interface Props {
  dialogue: string;
  hints: string[];
  correctFeedback: string;
  incorrectFeedback: string;
  beat: Beat;
  hintTier: number;                                   // -1 = dialogue, 0..n-1 = hint
  whisperLabel: string;                               // e.g. "Finn flüstert"
  side?: 'right' | 'left';                            // tail direction
  maxChars?: number;
}

export default function DialogueBubble({
  dialogue, hints, correctFeedback, incorrectFeedback, beat, hintTier,
  whisperLabel, side = 'right', maxChars = BUBBLE_MAX_CHARS,
}: Props) {
  const content = useMemo(() => resolveCallout({
    dialogue, hints, correctFeedback, incorrectFeedback, beat, hintTier,
  }), [dialogue, hints, correctFeedback, incorrectFeedback, beat, hintTier]);

  // Fade-swap animation key — bumps whenever the text/kind changes
  const [animKey, setAnimKey] = useState(0);
  useEffect(() => { setAnimKey((k) => k + 1); }, [content.text, content.kind]);

  if (!content.text) return null;
  const display = truncate(content.text, maxChars);

  const label = content.kind === 'hint' ? whisperLabel : null;
  const palette = paletteFor(content.kind);

  const tailStyle: React.CSSProperties = side === 'right'
    ? { left: -10, borderWidth: '10px 12px 10px 0', borderColor: `transparent ${palette.bg} transparent transparent` }
    : { right: -10, borderWidth: '10px 0 10px 12px', borderColor: `transparent transparent transparent ${palette.bg}` };

  return (
    <div
      style={{
        position: 'relative',
        maxWidth: 260,
        padding: '12px 14px 14px',
        background: palette.bg,
        border: `1.5px solid ${palette.border}`,
        borderRadius: 14,
        boxShadow: '0 4px 14px rgba(60,50,40,0.10)',
        fontFamily: 'var(--serif, Georgia, serif)',
        color: 'var(--ink, #3c3228)',
        animation: `adv-fade-swap ${FADE_SWAP_MS}ms ease-out both`,
      }}
      key={animKey}
      role="status"
      aria-live={content.kind === 'feedback' ? 'assertive' : 'polite'}
    >
      <div style={{ position: 'absolute', top: 16, width: 0, height: 0, ...tailStyle }} />

      {label && (
        <div style={{
          fontFamily: 'var(--mono, ui-monospace, monospace)',
          fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase',
          color: palette.label, marginBottom: 6,
        }}>
          {label}
        </div>
      )}

      <div style={{ fontSize: 15, lineHeight: 1.42, fontStyle: content.kind === 'feedback' ? 'italic' : 'normal' }}>
        {display}
      </div>
    </div>
  );
}

function paletteFor(kind: 'dialogue' | 'hint' | 'feedback') {
  if (kind === 'hint') {
    return { bg: '#fef6e2', border: '#e8cf9a', label: '#a27a2a' };
  }
  if (kind === 'feedback') {
    return { bg: '#fff8ea', border: '#e6d6b8', label: '#8a7a6a' };
  }
  return { bg: '#fffdf5', border: '#e5dcc8', label: '#8a7a6a' };
}
